import React, { useRef, useEffect, useCallback, useState } from 'react';
import EquationModal from '../EquationModal';
import { sanitizeRichHtml, pasteToPlainText } from '../../utils/sanitizeContent';

const TOOLBAR = [
  { icon: 'B', cmd: 'bold', title: 'Bold', btnStyle: { fontWeight: 700 } },
  { icon: 'I', cmd: 'italic', title: 'Italic', btnStyle: { fontStyle: 'italic' } },
  { icon: 'U', cmd: 'underline', title: 'Underline', btnStyle: { textDecoration: 'underline' } },
  { sep: true },
  { icon: '•≡', cmd: 'insertUnorderedList', title: 'Bullet list' },
  { icon: '1≡', cmd: 'insertOrderedList', title: 'Numbered list' },
  { sep: true },
  { icon: '←⊣', cmd: 'outdent', title: 'Outdent' },
  { icon: '⊢→', cmd: 'indent', title: 'Indent' },
  { sep: true },
  { icon: '✕', cmd: 'removeFormat', title: 'Clear formatting', btnStyle: { color: '#ef4444' } },
];

/**
 * RichEditor - A WYSIWYG editor with optional equation support
 *
 * @param {string} value - The HTML content
 * @param {function} onChange - Called when content changes
 * @param {string} placeholder - Placeholder text
 * @param {number} minHeight - Minimum height of the editor
 * @param {boolean} showEquationButton - Whether to show the equation button (default: false)
 */
export default function RichEditor({
  value,
  onChange,
  placeholder,
  minHeight = 120,
  showEquationButton = false,
}) {
  const ref = useRef(null);
  const lastValue = useRef(null);
  const savedSelection = useRef(null);
  const [showEquationModal, setShowEquationModal] = useState(false);

  // Initialise once on mount
  useEffect(() => {
    if (ref.current) {
      ref.current.innerHTML = value ?? '';
      lastValue.current = value ?? '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync when the parent resets the value (e.g. Cancel)
  useEffect(() => {
    if (ref.current && value !== lastValue.current) {
      ref.current.innerHTML = value ?? '';
      lastValue.current = value ?? '';
    }
  }, [value]);

  // Run the editor's innerHTML through our sanitizer before bubbling up.
  // Mobile renders Markdown + $$LaTeX$$ — anything beyond a small set of
  // tags (b/i/u/p/div/br/ul/ol/li + <span class="math-equation">) is noise
  // that ends up displayed as raw markup in the app. Strip it here so the
  // saved row is what the app expects.
  const onInput = useCallback(() => {
    if (!ref.current) return;
    const sanitized = sanitizeRichHtml(ref.current.innerHTML);
    lastValue.current = sanitized;
    onChange(sanitized);
  }, [onChange]);

  const exec = useCallback(
    cmd => {
      ref.current?.focus();
      document.execCommand(cmd, false, null);
      if (ref.current) {
        const sanitized = sanitizeRichHtml(ref.current.innerHTML);
        lastValue.current = sanitized;
        onChange(sanitized);
      }
    },
    [onChange]
  );

  // Intercept paste so Word / Google Docs / styled web content can't inject
  // their inline styles. We grab the plain text and re-insert it via
  // execCommand so cursor position stays correct.
  const onPaste = useCallback(
    e => {
      e.preventDefault();
      const clipboard = e.clipboardData || window.clipboardData;
      if (!clipboard) return;
      const html = clipboard.getData('text/html');
      const text = clipboard.getData('text/plain');
      // Prefer the HTML payload (preserves intentional line breaks) when
      // present, otherwise fall back to plain text.
      const plain = html ? pasteToPlainText(html) : text;
      if (!plain) return;
      // execCommand('insertText') is the only way to keep undo history
      // intact when programmatically writing into a contenteditable.
      document.execCommand('insertText', false, plain);
    },
    []
  );

  // Save the current selection/cursor position
  const saveSelection = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      savedSelection.current = selection.getRangeAt(0).cloneRange();
    }
  }, []);

  // Restore the saved selection/cursor position
  const restoreSelection = useCallback(() => {
    if (savedSelection.current && ref.current) {
      ref.current.focus();
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(savedSelection.current);
      }
    }
  }, []);

  // Handle equation button click - save selection before opening modal
  const handleEquationClick = useCallback(
    e => {
      e.preventDefault();
      saveSelection();
      setShowEquationModal(true);
    },
    [saveSelection]
  );

  // Insert equation as LaTeX wrapped in delimiters
  const handleInsertEquation = useCallback(
    latex => {
      if (!ref.current) return;

      // Restore the saved cursor position
      restoreSelection();

      // Insert LaTeX wrapped in $$ delimiters for display math
      // Use contenteditable="false" to prevent typing inside the equation
      // Add a zero-width space after to ensure cursor can be placed after it
      const latexHtml = `<span class="math-equation" contenteditable="false" style="font-family: 'Times New Roman', serif; font-style: italic; background: #f0f9ff; padding: 2px 6px; border-radius: 4px; margin: 0 2px; display: inline-block;">$$${latex}$$</span>&#8203;`;

      // Use insertHTML command to insert at cursor position
      document.execCommand('insertHTML', false, latexHtml);

      // Move cursor after the inserted content
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.collapse(false); // Collapse to end
        selection.removeAllRanges();
        selection.addRange(range);
      }

      if (ref.current) {
        lastValue.current = ref.current.innerHTML;
        onChange(ref.current.innerHTML);
      }

      // Clear saved selection
      savedSelection.current = null;
    },
    [onChange, restoreSelection]
  );

  return (
    <div className="rich-editor">
      {/* Toolbar */}
      <div className="rich-toolbar">
        {TOOLBAR.map((item, i) =>
          item.sep ? (
            <span key={i} className="rich-sep" />
          ) : (
            <button
              key={item.cmd}
              type="button"
              title={item.title}
              className="rich-btn"
              style={item.btnStyle}
              onMouseDown={e => {
                e.preventDefault(); // keep focus in editor
                exec(item.cmd);
              }}
            >
              {item.icon}
            </button>
          )
        )}

        {/* Equation button - only shown when enabled */}
        {showEquationButton && (
          <>
            <span className="rich-sep" />
            <button
              type="button"
              title="Insert Equation (LaTeX)"
              className="rich-btn rich-btn-equation"
              onMouseDown={handleEquationClick}
            >
              ∑
            </button>
          </>
        )}
      </div>

      {/* Editable body */}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        className="rich-body"
        style={{ minHeight }}
        onInput={onInput}
        onPaste={onPaste}
        data-placeholder={placeholder || 'Write here…'}
      />

      {/* Equation Modal */}
      <EquationModal
        isOpen={showEquationModal}
        onClose={() => setShowEquationModal(false)}
        onInsert={handleInsertEquation}
      />
    </div>
  );
}
